import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import {
  fetchAgreementTemplate,
  fetchMarkets,
  formatMarketDateOption,
  submitApplication,
  type Market,
  type VendorCategoryOption,
} from '@/api/applicationsApi'
import { ApiClientError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { MarkdownDocument } from '@/components/forms/MarkdownDocument'
import { SignaturePad } from '@/components/forms/SignaturePad'

import styles from './PortalPages.module.css'
import authStyles from './AuthPages.module.css'

type VendorCategory = 'commercial' | 'school_community' | 'learner_classroom'

/** Indemnity / vendor application form with signature capture. */
export function ApplicationFormPage() {
  const { user, isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [markets, setMarkets] = useState<Market[]>([])
  const [marketId, setMarketId] = useState<number | ''>('')
  const [agreementMarkdown, setAgreementMarkdown] = useState('')
  const [categories, setCategories] = useState<VendorCategoryOption[]>([])
  const [form, setForm] = useState({
    applicant_name: user?.full_name ?? '',
    business_name: user?.business_name ?? '',
    email: user?.email ?? '',
    cellphone: user?.whatsapp_number ?? '',
    identity_number: '',
    items_for_sale: '',
    vendor_category: 'commercial' as VendorCategory,
    supervising_teacher: '',
    electricity_needed: false,
    agreement_accepted: false,
    signatory_name: user?.full_name ?? '',
    signed_on: new Date().toISOString().slice(0, 10),
    signature_data: '',
  })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const [marketsResult, agreement] = await Promise.all([
          fetchMarkets(),
          fetchAgreementTemplate(),
        ])
        setMarkets(marketsResult.markets)
        if (marketsResult.markets[0]) setMarketId(marketsResult.markets[0].id)
        setAgreementMarkdown(agreement.markdown)
        setCategories(agreement.vendor_categories)
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load application form.')
      }
    })()
  }, [])

  const selected = useMemo(
    () => markets.find((market) => market.id === marketId) ?? null,
    [markets, marketId],
  )

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (isAdmin) return <Navigate to="/admin" replace />

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!marketId) {
      setError('Select a market.')
      return
    }
    if (form.vendor_category === 'learner_classroom' && !form.supervising_teacher.trim()) {
      setError('Supervising teacher is required for learner / classroom markets.')
      return
    }
    if (!form.signature_data) {
      setError('Please sign the form.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await submitApplication({
        market_id: marketId,
        applicant_name: form.applicant_name,
        business_name: form.business_name,
        email: form.email,
        cellphone: form.cellphone,
        identity_number: form.identity_number || null,
        items_for_sale: form.items_for_sale,
        vendor_category: form.vendor_category,
        supervising_teacher:
          form.vendor_category === 'learner_classroom' ? form.supervising_teacher : null,
        electricity_needed: form.electricity_needed,
        agreement_accepted: form.agreement_accepted,
        signatory_name: form.signatory_name,
        signed_on: form.signed_on,
        signature_data: form.signature_data,
      })
      navigate('/vendor')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not submit application.')
    } finally {
      setBusy(false)
    }
  }

  const fee = selected
    ? form.electricity_needed
      ? selected.fee_with_electricity
      : selected.fee_standard
    : 100

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>Vendor application & indemnity</h1>
          <p className={styles.lead}>
            Complete the application, review the full terms, and sign. Fee R
            {selected?.fee_standard ?? 100}, or R{selected?.fee_with_electricity ?? 150} if electricity
            is needed.
          </p>
        </div>
        <Link className={styles.ghost} to="/vendor">
          Back
        </Link>
      </header>

      <form className={styles.formGrid} onSubmit={onSubmit}>
        <label className={authStyles.label}>
          Market date
          <select
            className={authStyles.select}
            value={marketId}
            onChange={(e) => setMarketId(Number(e.target.value))}
            required
          >
            {markets.length === 0 ? (
              <option value="">No upcoming market dates available</option>
            ) : null}
            {markets.map((market) => (
              <option key={market.id} value={market.id}>
                {formatMarketDateOption(market)}
              </option>
            ))}
          </select>
        </label>

        <label className={authStyles.label}>
          Vendor category
          <select
            className={authStyles.select}
            value={form.vendor_category}
            onChange={(e) => update('vendor_category', e.target.value as VendorCategory)}
            required
          >
            {(categories.length
              ? categories
              : [
                  { value: 'commercial' as const, label: 'Commercial Vendor' },
                  {
                    value: 'school_community' as const,
                    label: 'School Community Vendor',
                  },
                  {
                    value: 'learner_classroom' as const,
                    label: 'Learner / Classroom Market',
                  },
                ]
            ).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {form.vendor_category === 'learner_classroom' ? (
          <label className={authStyles.label}>
            Supervising teacher
            <input
              className={authStyles.input}
              value={form.supervising_teacher}
              onChange={(e) => update('supervising_teacher', e.target.value)}
              required
            />
          </label>
        ) : null}

        <label className={authStyles.label}>
          Name
          <input
            className={authStyles.input}
            value={form.applicant_name}
            onChange={(e) => update('applicant_name', e.target.value)}
            required
          />
        </label>
        <label className={authStyles.label}>
          Business
          <input
            className={authStyles.input}
            value={form.business_name}
            onChange={(e) => update('business_name', e.target.value)}
            required
          />
        </label>
        <label className={authStyles.label}>
          Identity / registration number (optional)
          <input
            className={authStyles.input}
            value={form.identity_number}
            onChange={(e) => update('identity_number', e.target.value)}
          />
        </label>
        <label className={authStyles.label}>
          Email
          <input
            className={authStyles.input}
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            required
          />
        </label>
        <label className={authStyles.label}>
          Cellphone Number
          <input
            className={authStyles.input}
            value={form.cellphone}
            onChange={(e) => update('cellphone', e.target.value)}
            required
          />
        </label>
        <label className={authStyles.label}>
          Please list items for sale
          <textarea
            className={authStyles.textarea}
            value={form.items_for_sale}
            onChange={(e) => update('items_for_sale', e.target.value)}
            required
          />
        </label>

        <label className={styles.check}>
          <input
            type="checkbox"
            checked={form.electricity_needed}
            onChange={(e) => update('electricity_needed', e.target.checked)}
          />
          Electricity needed (fee R{selected?.fee_with_electricity ?? 150} instead of R
          {selected?.fee_standard ?? 100})
        </label>

        <div className={styles.notice}>
          <p>
            Fee of <strong>R{fee.toFixed(2)}</strong> to be paid on or before{' '}
            {selected?.application_deadline ?? 'the published closing date'} into{' '}
            {selected?.bank_name ?? 'FNB'} account{' '}
            <strong>{selected?.bank_account ?? '63044084638'}</strong>.
          </p>
          <p>Reference: {selected?.payment_reference_hint ?? 'Name + market date'}</p>
          <p>
            After submitting, upload your proof of payment (PDF or photo) from the Vendor area. You
            can also send it via WhatsApp to {selected?.submit_whatsapp ?? '+27 62 424 1804'}.
          </p>
        </div>

        <div className={styles.agreement}>
          <h2>Vendor application, terms & conditions, and indemnity</h2>
          <p className={styles.meta}>
            Please read the full agreement carefully before signing. This is the same document used
            for the downloadable PDF.
          </p>
          <div className={styles.agreementScroll}>
            {agreementMarkdown ? (
              <MarkdownDocument markdown={agreementMarkdown} />
            ) : (
              <p>Loading agreement…</p>
            )}
          </div>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={form.agreement_accepted}
              onChange={(e) => update('agreement_accepted', e.target.checked)}
              required
            />
            I declare that I have read this Agreement in its entirety and agree to comply with all
            its terms and conditions
          </label>
        </div>

        <label className={authStyles.label}>
          Name and Surname
          <input
            className={authStyles.input}
            value={form.signatory_name}
            onChange={(e) => update('signatory_name', e.target.value)}
            required
          />
        </label>
        <label className={authStyles.label}>
          Date
          <input
            className={authStyles.input}
            type="date"
            value={form.signed_on}
            onChange={(e) => update('signed_on', e.target.value)}
            required
          />
        </label>
        <div>
          <p className={styles.signLabel}>Signature</p>
          <SignaturePad onChange={(value) => update('signature_data', value)} />
        </div>

        {error ? <p className={styles.error}>{error}</p> : null}
        <button className={authStyles.submit} type="submit" disabled={busy}>
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </form>
    </section>
  )
}
